/**
 * Avatar de pessoa (framedimagestyle do original): a imagem que a pessoa
 * escolheu ou, sem ela, um círculo com a cor da conta e a inicial; com o ponto de
 * presença opcional. Com `video` (a webcam da pessoa na conferência, BRAZ-20), o vídeo
 * ao vivo toma o lugar da imagem.
 */
import { useEffect, useRef, useState } from 'react';
import { AVATAR_DRAG_NAME, avatarSrc } from './avatarUrl.js';
import type { PresenceDot } from './friendsModel.js';
import './avatar.css';
import { translate, useLanguage } from '../i18n/index.js';

export function Avatar({
  name,
  color,
  size = 40,
  presence,
  userId,
  avatarVersion = 0,
  room,
  dragName,
  video,
}: {
  name: string;
  color: string;
  size?: number;
  /** o ponto de presença (amigos): verde, laranja (ausente), vermelho com um traço (ocupado) ou cinza */
  presence?: PresenceDot;
  userId?: string;
  avatarVersion?: number;
  /** a mesa e a versão da foto da pessoa nela (0 = vale a do perfil) */
  room?: { id: string; avatarVersion: number };
  /** o nome que vai junto quando a foto é arrastada (padrão: `name`) */
  dragName?: string;
  /** a webcam da pessoa na conferência: aparece no lugar da foto */
  video?: MediaStream | null;
}) {
  const [broken, setBroken] = useState<string | null>(null);
  const src = avatarSrc(userId, avatarVersion, room);
  const showImage = src !== null && broken !== src;
  return (
    <span
      className="rd-avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), background: showImage || video ? '#1d1d1d' : `radial-gradient(circle at 35% 30%, ${color}, #1d1d1d 125%)` }}
      aria-hidden="true"
    >
      {video ? <AvatarVideo stream={video} /> : showImage ? <img className="rd-avatar__img" src={src} alt="" onError={() => setBroken(src)} onDragStart={(e) => e.dataTransfer.setData(AVATAR_DRAG_NAME, dragName ?? name)} /> : name.trim().slice(0, 1).toUpperCase() || '?'}
      {presence ? <span className={`rd-avatar__dot${presence === 'offline' ? '' : ` is-${presence}`}`} /> : null}
    </span>
  );
}

/** A webcam ao vivo no círculo (sempre muda: o som sai do ConferenceAudio). */
function AvatarVideo({ stream }: { stream: MediaStream }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && el.srcObject !== stream) el.srcObject = stream;
  }, [stream]);
  return <video ref={ref} className="rd-avatar__video" autoPlay playsInline muted />;
}

/**
 * Lado máximo da foto guardada. Os círculos são pequenos, mas "Ver foto", no menu do cartão de
 * Participantes, mostra a foto grande: até 2026-10-22 o lado era 256, e essas ficam borradas ali.
 */
export const AVATAR_MAX_SIDE = 768;

/** Reduz a imagem escolhida para no máximo AVATAR_MAX_SIDE de lado (recorte quadrado no centro). */
export async function shrinkAvatar(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const size = Math.min(AVATAR_MAX_SIDE, side);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('sem canvas');
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
  if (blob) return blob;
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error(translate(useLanguage.getState().language, 'error.badImage')))), 'image/png'));
}
